/**
 * Version Control Utility
 *
 * Feature: 003-bug-fixes
 * Implements optimistic locking for concurrent edit detection.
 */
import 'firebase/compat/firestore';
/**
 * Check if a document has version metadata.
 */
export function hasVersionMetadata(doc) {
    return (typeof doc.version === 'number' &&
        doc.updatedAt !== undefined &&
        typeof doc.updatedBy === 'string');
}
/**
 * Get version from document, defaulting to 0 for unversioned documents.
 */
export function getDocumentVersion(doc) {
    return doc.version ?? 0;
}
/**
 * Create version metadata for a new save.
 * Note: serverTimestamp should be obtained from Firestore at call site.
 */
export function createVersionMetadata(currentVersion, userId) {
    // Return with a placeholder - actual serverTimestamp() call happens at save time
    return {
        version: currentVersion + 1,
        updatedAt: null, // Caller will set serverTimestamp()
        updatedBy: userId,
    };
}
/**
 * Generate user-friendly error message for version conflict (Czech).
 */
export function formatVersionConflictError(_conflict) {
    return `Někdo jiný upravil tento rozvrh. Prosím obnovte stránku a zkuste to znovu.`;
}
/**
 * Generate English error message (for logging).
 */
export function formatVersionConflictErrorEn(conflict) {
    return `Version conflict: Document was modified by another user. ` +
        `Expected version ${conflict.attemptedVersion}, ` +
        `but current version is ${conflict.currentVersion}. ` +
        `Last updated by ${conflict.lastUpdatedBy}.`;
}
/**
 * Check if save should proceed based on version.
 * Returns null if OK, or a VersionConflict if there's a mismatch.
 */
export function checkVersionConflict(expectedVersion, currentDoc) {
    if (!currentDoc) {
        // New document, no conflict possible if expected version is 0
        if (expectedVersion === 0)
            return null;
        // Otherwise, document was deleted
        return {
            code: 'VERSION_CONFLICT',
            message: 'Dokument byl smazán jiným uživatelem.',
            currentVersion: -1,
            attemptedVersion: expectedVersion,
            lastUpdatedBy: 'unknown',
            lastUpdatedAt: null,
        };
    }
    const currentVersion = getDocumentVersion(currentDoc);
    if (currentVersion !== expectedVersion) {
        return {
            code: 'VERSION_CONFLICT',
            message: formatVersionConflictError({}),
            currentVersion,
            attemptedVersion: expectedVersion,
            lastUpdatedBy: currentDoc.updatedBy || 'unknown',
            lastUpdatedAt: currentDoc.updatedAt || null,
        };
    }
    return null; // No conflict
}
